//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState } from 'react';
import { Bookmark, BookmarkBorderOutlined } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getSalaryRateText, getFirstOccupationText, getCityStateText } from '../../common/textHelper';
// import { saveJobSeekerToJobs } from '../../common/services/empDashboardServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
// import { fetchEmployerActiveJobs, loadConversations } from '../../common/services/talentCenterServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SaveJobSeekerToMyJobsPopover from './SaveJobSeekerToMyJobsPopover';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import clsx from 'clsx';
// import _get from 'lodash/get';
import _size from 'lodash/size';
// import _some from 'lodash/some';
// import _map from 'lodash/map';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        card: {
            borderLeft: '4px solid transparent',
            display: 'flex',
            padding: '15px',
            cursor: 'pointer',
        },
        activeCard: {
            borderLeft: '4px solid #1D8A8B',
            background: 'rgba(0, 0, 0, 0.03)',
        },
        logo: {
            marginRight: '10px',
            width: 60,
            height: 60,
            borderRadius: '30px',
            border: '1px solid #D5D7DD',
            objectFit: 'cover',
            margin: 0,
            color: 'rgb(252,130,31)',
            background: 'rgba(252, 130, 31, 0.1)',
            fontSize: '35px',
        },
        middleSection: {
            flex: 1,
        },
        name: {
            display: 'block',

            fontWeight: 'bold',
            color: '#111e14',
        },
        address: {
            display: 'block',
            color: '#9b9e9c',
        },
        salary: {
            color: '#9b9e9c',
        },
        type: {
            color: '#9b9e9c',
        },
        publishedOn: {
            fontSize: '13px',
            color: '#9b9e9c',
            display: 'block',
        },
        icon: {
            marginTop: '15px',
            float: 'right',
        },
        salaryItemDiv: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        currencyIcon: {
            color: '#9b9e9c',
            marginRight: '4px',
            fontSize: '15px',
            lineHeight: '22px',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        // appContext,
        // layoutContext,
        isJobSeekerAlreadySaved,
        // popoverOpen,
        // anchorEl,
        // setAnchorEl,
        // popoverId,
        // setPopoverOpen,
        // handleSavePosting,
        // jobListings,
        // bookmarkedJob,
        // setBookmarkedJob,
        // handleSaveToJobs,
        // jobSeeker,
    } = params;

    // const bookmarkedJobs = _get(appContext, 'currentUser.bookmarkedJobs', []);
    // const bookmarked = bookmarkedJobs?.includes(props.uid); //uid is the job id

    const { card: cardDefaultClass, activeCard: cardActiveClass } = classes;

    const defns = {
        card: {
            className: clsx(cardDefaultClass, { [cardActiveClass]: props.selected }),
            onClick: () => {
                props.setSelected(props.uid);
            },
        },

        logo: { src: props.profilePicUrl },
        icon: {
            children: isJobSeekerAlreadySaved ? <Bookmark style={{ color: '#FC821F' }} /> : <BookmarkBorderOutlined />,
            // children: <Tooltip title="Save to My Jobs">{isJobSeekerAlreadySaved ? <Bookmark style={{ color: '#FC821F' }} /> : <BookmarkBorderOutlined />}</Tooltip>,
            // onClick: handleSaveToJobs,
        },

        name: {
            variant: 'body1',
        },
        address: {
            variant: 'body2',
        },
        salary: {
            variant: 'body2',
        },
        type: {
            variant: 'body2',
        },
        // saveJobSeekerToJobsPopover: {
        //     popoverOpen,
        //     anchorEl,
        //     setAnchorEl,
        //     id: popoverId,
        //     setPopoverOpen,
        //     handleSavePosting,
        //     jobListings,
        //     bookmarkedJob,
        //     setBookmarkedJob,
        //     jobSeeker,
        // },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const UserListItem = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    const layoutContext = useContext(LayoutContext);
    // const employerData = _get(appContext, 'currentEmployer');
    // const employerId = _get(appContext, 'employerId');
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [anchorEl, setAnchorEl] = useState(null);
    const [popoverOpen, setPopoverOpen] = useState(Boolean(anchorEl));
    // const [isJobSeekerAlreadySaved, setIsJobSeekerAlreadySaved] = useState(false);
    // const [conversations, setConversations] = useState([]);
    // const [jobListings, setJobListings] = useState([]);
    // const [bookmarkedJob, setBookmarkedJob] = useState([]);
    // const [userProfileInfo, setUserProfileInfo] = useState({});
    // const [jobSeeker, setJobSeeker] = useState({});

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const {
        country,
        state,
        city,
        occupation: occupationList,
        isExactRate,
        rateMin,
        rateMax,
        currency,
        currencySymbol,
        payFrequency,
        employmentType,
        savedToJobs,
        resumeList,
    } = props;
    const occupation = getFirstOccupationText(occupationList, resumeList);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const popoverId = popoverOpen ? 'simple-popover' : undefined;
    const isJobSeekerAlreadySaved = _size(savedToJobs) > 0;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // useEffect(() => {
    //     setIsJobSeekerAlreadySaved(false);
    //     if (Array.isArray(employerData?.savedJs)) {
    //         employerData.savedJs.forEach(element => {
    //             if (element === props.userId) {
    //                 setIsJobSeekerAlreadySaved(true);
    //             }
    //         });
    //     }
    //     //eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [employerData, employerData?.savedJs?.length]);

    // useEffect(() => {
    //     console.log({ props });
    // }, [props]);

    // useEffect(() => {
    //     setJobSeeker({ id: userId });
    // }, [userId]);

    // useEffect(() => {
    //     let unsubscribeLoadConversations = null;
    //     let unsubscribeFetchEmployerActiveJobs = null;
    //     if (employerId) {
    //         unsubscribeLoadConversations = loadConversations(employerId, setConversations);
    //         unsubscribeFetchEmployerActiveJobs = fetchEmployerActiveJobs(setJobListings, employerId);
    //     }
    //     return () => {
    //         unsubscribeRealtimeUpdates([unsubscribeLoadConversations, unsubscribeFetchEmployerActiveJobs]);
    //     };
    //     //eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [employerId]);

    // useEffect(() => {
    //     if (userId) {
    //         fetchUserProfile(userId, setUserProfileInfo);
    //     }
    //     //eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [userId]);

    // useEffect(() => {
    //     if (Array.isArray(userProfileInfo.occupation)) {
    //         setOccupation(userProfileInfo.occupation[0]);
    //     }
    //     // console.log({ userProfileInfo });
    // }, [userProfileInfo]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    // const handleSaveToJobs = event => {
    //     setAnchorEl(event.currentTarget);
    //     setPopoverOpen(true);
    // };

    // const handleSavePosting = () => {
    //     const jobsToUnsub = _filter(jobListings, job => !_some(bookmarkedJob, bj => job.uid === bj.uid));
    //     const jobSeekerData = {
    //         email: props.email || '',
    //         firstName: props.firstName,
    //         lastName: props.lastName,
    //         profilePicUrl: props.profilePicUrl,
    //         userId: props.userId,
    //     };

    //     const jobsToSave = _map(bookmarkedJob, job => ({ jobId: job.uid }));
    //     const jobsToUnSave = _map(jobsToUnsub, job => ({ jobId: job.uid }));

    //     saveJobSeekerToJobs({ employerId, jobSeekerData, jobsToSave, jobsToUnSave, setPopoverOpen, setSnackbarMessage, setSnackbarErrorMessage });
    // };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        appContext,
        layoutContext,
        isJobSeekerAlreadySaved,
        popoverOpen,
        anchorEl,
        setAnchorEl,
        popoverId,
        setPopoverOpen,
        // handleSavePosting,
        // jobListings,
        // bookmarkedJob,
        // setBookmarkedJob,
        // handleSaveToJobs,
        // jobSeeker,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.card}>
            <div>
                <AfitdAvatar alt="dp" {...defns.logo}>
                    ?
                </AfitdAvatar>
            </div>
            <div {...defns.middleSection}>
                <AfitdTypography {...defns.name}>{occupation}</AfitdTypography>

                <AfitdTypography {...defns.address}>{getCityStateText(city, state)}</AfitdTypography>
                <AfitdTypography {...defns.address}>{country}</AfitdTypography>
                <div {...defns.salaryItemDiv}>
                    <div {...defns.currencyIcon}>{currencySymbol} </div>

                    <AfitdTypography {...defns.salary}> {getSalaryRateText(isExactRate, rateMin, rateMax, currency, payFrequency)} </AfitdTypography>
                </div>
                <div {...defns.salaryItemDiv}>
                    <AfitdTypography {...defns.type}>{employmentType?.join(', ')}</AfitdTypography>
                </div>
            </div>
            <div>
                <div {...defns.icon} />
            </div>
            {/* <SaveJobSeekerToMyJobsPopover {...defns.saveJobSeekerToJobsPopover} /> */}
        </div>
    );
};

export default UserListItem;
